import React from "react";
import { CheckCircle2, Circle } from "lucide-react";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, sub, tone = "slate", testid }) {
  const toneMap = {
    slate: "bg-white border-slate-200",
    lime: "bg-lime-50 border-lime-200",
    navy: "bg-slate-900 border-slate-900 text-white",
    amber: "bg-amber-50 border-amber-200",
    rose: "bg-rose-50 border-rose-200",
  };
  const labelTone = tone === "navy" ? "text-lime-300" : "text-slate-500";
  const subTone = tone === "navy" ? "text-slate-300" : "text-slate-500";
  return (
    <div data-testid={testid} className={`rounded-2xl border p-5 ${toneMap[tone]}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${labelTone}`}>{label}</div>
      <div className="mt-1.5 font-display text-2xl md:text-3xl font-bold leading-none">{value}</div>
      {sub && <div className={`mt-1.5 text-xs ${subTone}`}>{sub}</div>}
    </div>
  );
}

export function Chip({ children, tone = "slate" }) {
  const map = {
    slate: "bg-slate-100 text-slate-700",
    lime: "bg-lime-100 text-lime-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
    emerald: "bg-emerald-100 text-emerald-800",
    sky: "bg-sky-100 text-sky-800",
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map[tone]}`}>{children}</span>;
}

export function ToggleContacted({ value, onChange, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onChange}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
        value ? "bg-lime-100 text-lime-800" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
      }`}
    >
      {value ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
      {value ? "Contacted" : "Not contacted"}
    </button>
  );
}

export function EmptyState({ title, hint }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center">
      <div className="font-display text-lg font-semibold text-slate-900">{title}</div>
      {hint && <div className="mt-1 text-sm text-slate-500">{hint}</div>}
    </div>
  );
}

export const FORM_LABEL = {
  newsletter: "Newsletter",
  brochure: "Brochure",
  calculator_report: "Calculator Report",
  contact: "Contact",
  register: "Registration",
  sponsor: "Sponsor Enquiry",
  exhibit: "Exhibitor Enquiry",
  speaker: "Speaker Proposal",
  nomination: "Award Nomination",
  market_report: "Market Report",
  prospectus: "Prospectus",
  sponsorship_proposal: "Sponsorship Proposal",
};

export const FORM_TONE = {
  register: "lime",
  brochure: "sky",
  sponsorship_proposal: "amber",
  market_report: "sky",
  prospectus: "sky",
  newsletter: "slate",
  contact: "slate",
  sponsor: "amber",
  exhibit: "amber",
  speaker: "emerald",
  nomination: "emerald",
  calculator_report: "sky",
};

export function ageBadge(dateStr) {
  const d = new Date(dateStr);
  const diffH = (Date.now() - d.getTime()) / 36e5;
  if (diffH < 1) return { tone: "lime", label: "Just now" };
  if (diffH < 24) return { tone: "lime", label: `${Math.floor(diffH)}h ago` };
  const days = Math.floor(diffH / 24);
  if (days < 3) return { tone: "amber", label: `${days}d ago` };
  return { tone: "rose", label: `${days}d ago` };
}

export function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function fmtMoney(amt, cur = "INR") {
  if (amt == null) return "—";
  const sym = cur === "INR" ? "₹" : cur === "USD" ? "$" : cur + " ";
  return sym + Number(amt).toLocaleString(cur === "INR" ? "en-IN" : "en-US");
}
